import React, { useEffect, useEffectEvent } from 'react';
import { clearQueryCache } from '../utils/userStorage';
import { appAnalytics } from '../services/analytics';

interface ErrorFallbackProps {
    error?: Error;
    errorInfo?: React.ErrorInfo;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, errorInfo }) => {
    const isDevelopment = import.meta.env.VITE_ENVIRONMENT !== 'production';

    // Clear persisted query cache on error to prevent stale/corrupted data from causing
    // repeated crashes. Only the IndexedDB cache — never the auth token, otherwise every
    // render error (e.g. a half-applied HMR update on the dev host) logs the user out.
    const logErrorPageView = useEffectEvent(() => {
        appAnalytics.logEvent('View ErrorPage', {
            error_message: error?.message,
            error_name: error?.name,
            error_stack: error?.stack?.slice(0, 500),
            component_stack: errorInfo?.componentStack?.slice(0, 500),
            url: window.location.href,
        });
    });

    useEffect(() => {
        clearQueryCache().catch(() => {});
        logErrorPageView();
    }, []);

    const handleReload = () => {
        window.location.reload();
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            backgroundColor: '#faf8f3',
            padding: '2rem',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
            <div style={{
                maxWidth: '500px',
                textAlign: 'center',
                animation: 'fadeIn 0.5s ease-in'
            }}>
                {/* Error Icon */}
                <div style={{
                    width: '80px',
                    height: '80px',
                    margin: '0 auto 2rem',
                    borderRadius: '50%',
                    backgroundColor: '#fff3cd',
                    border: '3px solid #d4a017',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2.5rem'
                }}>
                    ⚠️
                </div>

                {/* Error Message */}
                <h1 style={{
                    fontSize: '1.75rem',
                    fontWeight: '600',
                    color: '#2c2c2c',
                    marginBottom: '1rem',
                    lineHeight: '1.3'
                }}>
                    Something Went Wrong
                </h1>

                <p style={{
                    fontSize: '1rem',
                    color: '#666',
                    marginBottom: '2rem',
                    lineHeight: '1.6'
                }}>
                    We're sorry for the inconvenience. An unexpected error has occurred.
                    Please try reloading the app.
                </p>

                {/* Reload Button */}
                <button
                    onClick={handleReload}
                    style={{
                        backgroundColor: '#d4a017',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0.875rem 2rem',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 8px rgba(212, 160, 23, 0.2)'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#c49316';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(212, 160, 23, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#d4a017';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(212, 160, 23, 0.2)';
                    }}
                >
                    ↻ Reload App
                </button>

                <p style={{
                    fontSize: '0.8rem',
                    color: '#999',
                    marginTop: '1.5rem',
                    lineHeight: '1.5'
                }}>
                    If this error persists, <a
                        href="https://t.me/zhavoronkov"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#d4a017', textDecoration: 'underline' }}
                    >contact us</a>
                </p>

                {/* Error Details (Development Only) */}
                {isDevelopment && error && (
                    <details style={{
                        marginTop: '2rem',
                        textAlign: 'left',
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        padding: '1rem'
                    }}>
                        <summary style={{
                            cursor: 'pointer',
                            fontWeight: '600',
                            color: '#d4a017',
                            marginBottom: '0.5rem'
                        }}>
                            Error Details (Development Only)
                        </summary>
                        <div style={{
                            fontSize: '0.875rem',
                            color: '#333',
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            marginTop: '0.5rem'
                        }}>
                            <div style={{ marginBottom: '0.5rem' }}>
                                <strong>Error:</strong> {error.message}
                            </div>
                            {error.stack && (
                                <div style={{
                                    marginTop: '0.5rem',
                                    padding: '0.5rem',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    maxHeight: '200px',
                                    overflow: 'auto'
                                }}>
                                    {error.stack}
                                </div>
                            )}
                            {errorInfo?.componentStack && (
                                <div style={{
                                    marginTop: '0.5rem',
                                    padding: '0.5rem',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    maxHeight: '200px',
                                    overflow: 'auto'
                                }}>
                                    <strong>Component Stack:</strong>
                                    {errorInfo.componentStack}
                                </div>
                            )}
                        </div>
                    </details>
                )}
            </div>

            <style>
                {`
                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                            transform: translateY(10px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default ErrorFallback;
